import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, getErrMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ListPlus, Loader2 } from "lucide-react";

const YENI = "__yeni__";
const YOK = "__yok__";
const BOS = { hesap_kodu: "", kalem_adi: "", ana_kategori: "", alt_grup: "" };

export const KalemEkleDialog = ({ open, onClose, onSaved }) => {
  const [gruplar, setGruplar] = useState({ ana: [], alt: [] });
  const [form, setForm] = useState(BOS);
  const [yeniAna, setYeniAna] = useState("");
  const [yeniAlt, setYeniAlt] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchGruplar = useCallback(async () => {
    try {
      const res = await api.get("/gider-gruplari");
      setGruplar(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Gruplar yüklenemedi."));
    }
  }, []);

  useEffect(() => {
    if (open) {
      setForm(BOS);
      setYeniAna("");
      setYeniAlt("");
      fetchGruplar();
    }
  }, [open, fetchGruplar]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const kaydet = async () => {
    const anaAd = form.ana_kategori === YENI ? yeniAna.trim() : form.ana_kategori;
    const altAd = form.alt_grup === YENI ? yeniAlt.trim() : (form.alt_grup === YOK ? "" : form.alt_grup);
    if (!form.kalem_adi.trim()) return toast.error("Kalem adı zorunludur.");
    if (!anaAd) return toast.error("Ana grup seçin veya yeni grup adı yazın.");
    if (form.alt_grup === YENI && !altAd) return toast.error("Yeni alt grup adını yazın.");
    if (altAd && !form.hesap_kodu.trim()) return toast.error("Alt grup seçilen kalemlerde gider kalem numarası zorunludur.");
    setSaving(true);
    try {
      const grupEkle = async (ad, tip) => {
        try {
          await api.post("/gider-gruplari", { ad, tip });
        } catch (err) {
          if (err?.response?.status !== 400) throw err; // "zaten mevcut" dışındaki hatalar gösterilir
        }
      };
      if (form.ana_kategori === YENI) await grupEkle(anaAd, "ana");
      if (form.alt_grup === YENI) await grupEkle(altAd, "alt");
      await api.post("/butce-kalemleri", {
        kalem_adi: form.kalem_adi.trim(),
        hesap_kodu: form.hesap_kodu.trim(),
        ana_kategori: anaAd,
        alt_grup: altAd,
      });
      toast.success("Gider kalemi eklendi ve bütçe planına dahil edildi.");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(getErrMsg(err, "Kalem eklenemedi."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card sm:max-w-md" data-testid="kalem-ekle-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ListPlus className="h-5 w-5" /> Yeni Gider Kalemi
          </DialogTitle>
          <DialogDescription>
            Kalem otomatik olarak bütçe planına eklenir. Alt grup seçilirse kalem numarası zorunludur.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Gider Kalem No {((form.alt_grup && form.alt_grup !== YOK) ? "*" : "")}</Label>
              <Input
                placeholder="Örn: 770.02.05" value={form.hesap_kodu}
                onChange={(e) => set("hesap_kodu", e.target.value)}
                className="h-11 tabular-nums" data-testid="kalem-no-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Kalem Adı *</Label>
              <Input
                placeholder="Örn: Eğitim Giderleri" value={form.kalem_adi}
                onChange={(e) => set("kalem_adi", e.target.value)}
                className="h-11" data-testid="kalem-adi-input"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Ana Grup *</Label>
            <Select value={form.ana_kategori} onValueChange={(v) => set("ana_kategori", v)}>
              <SelectTrigger className="h-11" data-testid="kalem-ana-grup-select">
                <SelectValue placeholder="Seçin" />
              </SelectTrigger>
              <SelectContent className="bg-card">
                {gruplar.ana.map((g) => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
                <SelectItem value={YENI}>+ Yeni ana grup ekle…</SelectItem>
              </SelectContent>
            </Select>
            {form.ana_kategori === YENI && (
              <Input
                placeholder="Yeni ana grup adı (örn: İnsan ve Organizasyon)" value={yeniAna}
                onChange={(e) => setYeniAna(e.target.value)}
                className="h-11 mt-1" data-testid="kalem-yeni-ana-input"
              />
            )}
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Alt Grup (opsiyonel)</Label>
            <Select value={form.alt_grup} onValueChange={(v) => set("alt_grup", v)}>
              <SelectTrigger className="h-11" data-testid="kalem-alt-grup-select">
                <SelectValue placeholder="Alt grup yok" />
              </SelectTrigger>
              <SelectContent className="bg-card">
                <SelectItem value={YOK}>Alt grup yok</SelectItem>
                {gruplar.alt.map((g) => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
                <SelectItem value={YENI}>+ Yeni alt grup ekle…</SelectItem>
              </SelectContent>
            </Select>
            {form.alt_grup === YENI && (
              <Input
                placeholder="Yeni alt grup adı (örn: Brüt Maaş Ödemeleri)" value={yeniAlt}
                onChange={(e) => setYeniAlt(e.target.value)}
                className="h-11 mt-1" data-testid="kalem-yeni-alt-input"
              />
            )}
          </div>
          <Button onClick={kaydet} disabled={saving} className="min-h-[44px] w-full gap-1.5" data-testid="kalem-kaydet-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListPlus className="h-4 w-4" />}
            Kalemi Ekle
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
